export function isWeightProduct(product: any): boolean {
  return (product?.tipoDeVenda || '').toString().toLowerCase() === 'peso';
}

export function getProductDisplayPrice(product: any): number {
  return product?.valorMinimo || product?.preco || product?.valor || 0;
}

export function getWeightUnit(product: any): string {
  return (product?.unidadeMedida?.sigla || product?.unidadeMedida || 'kg').toString().trim() || 'kg';
}

export function getWeightIncrement(product: any): number {
  return positiveNumber(product?.incremento) || 1;
}

export function getWeightMinimum(product: any): number {
  return positiveNumber(product?.pesoMinimo) || 0;
}

export function getWeightMaximum(product: any): number {
  return positiveNumber(product?.pesoMaximo) || 0;
}

export function getInitialWeightQuantity(product: any): number {
  return positiveNumber(product?.valorInicial)
    || positiveNumber(product?.pesoMinimo)
    || positiveNumber(product?.incremento)
    || 1;
}

export function adjustWeightQuantity(product: any, currentQty: any, direction: number): number {
  const step = getWeightIncrement(product);
  const minimum = getWeightMinimum(product) || step || 1;
  const maximum = getWeightMaximum(product);
  const current = positiveNumber(currentQty) || getInitialWeightQuantity(product);
  const next = roundMoney(current + (direction * step));
  return normalizeWeightQuantity(product, next, minimum, maximum);
}

export function normalizeWeightQuantity(product: any, qty: any, fallback?: number, maxOverride?: number): number {
  const fallbackQty = positiveNumber(fallback) || getInitialWeightQuantity(product);
  const minimum = getWeightMinimum(product);
  const maximum = maxOverride !== undefined ? positiveNumber(maxOverride) : getWeightMaximum(product);
  let normalized = positiveNumber(qty) || fallbackQty;
  if (minimum > 0) normalized = Math.max(minimum, normalized);
  if (maximum > 0) normalized = Math.min(maximum, normalized);
  return roundWeight(normalized);
}

export function normalizeManualWeightQuantity(product: any, value: any): number {
  const parsed = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
  return normalizeWeightQuantity(product, parsed);
}

export function calculateCartItemTotal(product: any, qty: any, extrasTotal: number, discount: number): number {
  const basePrice = positiveNumber(product?.preco) || positiveNumber(product?.valor) || 0;
  const cleanQty = positiveNumber(qty) || 0;
  const cleanExtrasTotal = positiveNumber(extrasTotal) || 0;
  const cleanDiscount = positiveNumber(discount) || 0;

  if (isWeightProduct(product)) {
    const priceQty = getWeightUnit(product).toLowerCase() === 'g' ? cleanQty / 1000 : cleanQty;
    return Math.max(0, roundMoney((basePrice * priceQty) + cleanExtrasTotal - cleanDiscount));
  }

  return Math.max(0, roundMoney((basePrice + cleanExtrasTotal) * cleanQty - cleanDiscount));
}

export function formatWeightQuantity(product: any, qty: any): string {
  return `${formatWeightNumber(qty)} ${getWeightUnit(product)}`;
}

export function getWeightPriceUnitLabel(product: any): string {
  const unit = getWeightUnit(product).toLowerCase();
  return unit === 'g' ? 'KG' : unit.toUpperCase();
}

function positiveNumber(value: any): number {
  if (value === null || value === undefined || value === '') return 0;
  const number = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundWeight(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function formatWeightNumber(value: any): string {
  const number = positiveNumber(value);
  if (Number.isInteger(number)) return number.toString();
  return number.toFixed(3).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',');
}
